// Data access for the EA area, mirroring `../settings/api.js`'s
// ok/denied/disabled/error vocabulary rather than inventing a fifth one.
//
// `disabled` is the state this surface is expected to hit constantly right
// now: `CIPHER_PERSONAL_EA_V1` is off, so every `/api/me/ea*` route answers
// 404 (`routers/personal_ea.py`'s `_require_flag()`), and 404 is what
// `../settings/api.js` already treats as "not enabled" rather than an error.
// Reusing that mapping here means this surface renders the same honest
// "not enabled" state Settings already shows for the same router, instead of
// a red error screen for a router that is off by design.
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AX = { withCredentials: true };

function normalise(err) {
  const httpStatus = err && err.response && err.response.status;
  if (httpStatus === 404) {
    return { status: "disabled", data: null, httpStatus };
  }
  if (httpStatus === 403 || httpStatus === 401) {
    return { status: "denied", data: null, httpStatus };
  }
  return {
    status: "error",
    data: null,
    httpStatus,
    error: (err && err.response && err.response.data && err.response.data.detail) ||
      (err && err.message) ||
      "Request failed",
  };
}

export async function get(path) {
  try {
    const res = await axios.get(`${API}${path}`, AX);
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

export async function post(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, AX);
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

export const ENDPOINTS = {
  myEA: "/me/ea",
  onboarding: "/me/ea/onboarding",
  // Issue #331 stage 1. Both are the SAME conversation: `/chat` and the
  // Telegram webhook meet at `channels.turn`, and `/conversation` reads the
  // one transcript both write. The dock and the EA home therefore share a
  // history by construction rather than by two surfaces agreeing to.
  // Switching on/off, from Settings (#331). The backend has had these since
  // stage 1; nothing called them, so a finished questionnaire led nowhere.
  activate: "/me/ea/activate",
  suspend: "/me/ea/suspend",
  chat: "/me/ea/chat",
  chatStream: "/me/ea/chat/stream",
  conversation: "/me/ea/conversation",
  // Issue #331 stage 2. Both routes have existed since August with no
  // consumer, which is why these two tabs said "Not built yet" while the work
  // behind them was already finished and tested. Reading them was the gap.
  priorities: "/me/ea/priorities",
  followups: "/me/ea/email/followups",
};

/** Send a turn and receive the reply as it is written (#331).
 *
 * `fetch` with a reader rather than `EventSource`, for two reasons:
 * `EventSource` cannot POST, and it cannot carry credentials the way the
 * rest of this file does. The wire format is still server-sent events.
 *
 * `onDelta` is called with each fragment. The promise resolves with the
 * terminal event — `done` carries the authoritative reply, which is what a
 * caller should trust over anything it accumulated itself.
 */
export async function postStream(path, body, onDelta) {
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    return { status: "error", error: (err && err.message) || "Request failed" };
  }
  if (!res.ok) {
    if (res.status === 404) return { status: "disabled", httpStatus: 404 };
    if (res.status === 401 || res.status === 403) {
      return { status: "denied", httpStatus: res.status };
    }
    return { status: "error", httpStatus: res.status, error: "Request failed" };
  }
  if (!res.body || typeof res.body.getReader !== "function") {
    // No streaming in this environment. Not an error: the caller falls back
    // to the plain endpoint and the reply simply arrives all at once.
    return { status: "unsupported" };
  }

  const reader = res.body.getReader();
  const decode = new TextDecoder();
  let buffer = "";
  let terminal = null;

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decode.decode(value, { stream: true });
    // SSE frames are separated by a blank line; a partial frame stays in the
    // buffer until the rest of it arrives.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() || "";
    for (const frame of frames) {
      const line = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      let event;
      try {
        event = JSON.parse(payload);
      } catch (e) {
        continue;   // a display channel; a bad frame is skipped, not fatal
      }
      if (event.type === "delta") {
        if (onDelta) onDelta(event.text || "");
      } else {
        terminal = event;
      }
    }
  }
  if (!terminal) return { status: "error", error: "The reply ended early." };
  if (terminal.type === "error") {
    return { status: "error", error: terminal.error || "The reply failed.",
             reason: terminal.reason };
  }
  return { status: "ok", data: terminal };
}
